import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { SectionHeader, EASE } from "@/components/Reveal";

const ROWS = [
  { label: "Working Hours", old: "9 to 5, five days a week", neo: "24 hours a day, 365 days a year" },
  { label: "Costs", old: "Salaries, benefits, office space", neo: "A fraction of a single salary" },
  { label: "Speed", old: "Limited by human pace", neo: "Instant, parallel execution" },
  { label: "Availability", old: "Holidays, sick days, downtime", neo: "Always on. Never offline." },
  { label: "Scalability", old: "Months of hiring and training", neo: "Deploy new AI staff instantly" },
  { label: "Automation", old: "Manual, repetitive processes", neo: "Fully autonomous workflows" },
];

export default function Comparison() {
  return (
    <section
      id="why"
      data-testid="comparison-section"
      className="relative border-y border-white/5 bg-[#070707] py-32"
    >
      <div className="absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.06),transparent_65%)] blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <SectionHeader
          align="center"
          label="Why Alkyrvis"
          title={
            <>
              Why Hire More Staff When You Can{" "}
              <span className="gold-text">Build An AI Workforce?</span>
            </>
          }
        />

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="glass-panel mt-16 overflow-hidden rounded-2xl"
          data-testid="comparison-table"
        >
          <div className="grid grid-cols-[1fr_1fr_1fr] border-b border-white/5 bg-white/[0.02] max-md:grid-cols-[0.7fr_1fr_1fr]">
            <div className="px-5 py-5 md:px-8" />
            <div className="px-5 py-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] md:px-8 md:text-xs">
              Traditional Business
            </div>
            <div className="border-l border-gold/20 bg-gold/[0.05] px-5 py-5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold md:px-8 md:text-xs">
              Alkyrvis
            </div>
          </div>
          {ROWS.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.07, ease: EASE }}
              className="grid grid-cols-[1fr_1fr_1fr] border-b border-white/5 transition-colors duration-300 last:border-b-0 hover:bg-white/[0.02] max-md:grid-cols-[0.7fr_1fr_1fr]"
              data-testid={`comparison-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="px-5 py-5 text-xs font-medium uppercase tracking-wider text-[#F5F5F5] md:px-8 md:text-sm">
                {r.label}
              </div>
              <div className="flex items-start gap-2.5 px-5 py-5 text-xs text-[#777] md:px-8 md:text-sm">
                <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/25" />
                {r.old}
              </div>
              <div className="flex items-start gap-2.5 border-l border-gold/20 bg-gold/[0.05] px-5 py-5 text-xs text-[#E8E8E8] md:px-8 md:text-sm">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                {r.neo}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
